import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Box, Button, CircularProgress, Input, Stack, Tooltip } from "@mui/joy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonIcon from "@mui/icons-material/Person";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import CustomModal from "components/common/CustomModal";
import TabButtonList from "components/common/TabButtonList";
import { PATH } from "routes/paths";
import {
  ambassadorApi,
  type AmbassadorStatsBucket,
  clearAmbassadorToken,
  getAmbassadorToken,
} from "api/ambassadorApi";
import distancingLogo from "static/image/common/distancing_logo.png";
import CodeStatusChip, { StatusChip } from "pages/ambassador/CodeStatusChip";

type Tab = "daily" | "monthly";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

// 서버 검증과 동일 규칙 — 영문 소문자·숫자·하이픈·언더스코어 3~20자
const CODE_SLUG_REGEX = /^[a-z0-9_-]{3,20}$/;

// 계약 완료 후 해야 할 일 안내 배너 — 계약 유도 배너와 같은 자리·같은 모양으로, 누르면 해당 섹션으로 데려간다.
type GuideKey = "trial" | "link";
const GUIDE_BANNER_TEXTS: Record<GuideKey, string> = {
  trial:
    "체험 계정을 설정해 주세요 — 40만 원 상당의 프로그램을 무상으로 지원해 드려요 →",
  link: "링크를 만들어 주세요 — 링크로 결제가 발생할 때마다 약정된 수수료를 수익금으로 지급해 드려요 →",
};
// 이동 후 강조 시간 — 어디로 왔는지 알아볼 만큼만 짧게 유지한다
const GUIDE_HIGHLIGHT_MS = 1600;

// 앰버서더 환영 문구 후보 — 계약 전(시작 독려)/후(기여) 톤으로 나뉜다.
// 첫 문구는 계약 전후 공통 고정(브랜드 미션) — 진입 시 항상 이 문구부터 보이고, 이후 순환한다.
// "마음 돌봄"·"회복"·"공유"·"링크" 등 비공식·부적합 표현 금지.
// 계약 전에는 아직 앰버서더가 아니다 — 확정적 호칭 없이, 대의(마음 건강 기여)를 담은 기대 톤으로 쓴다.
// 계약 안내는 배너가 담당하므로 문구에서 계약을 직접 언급하지 않는다.
const WELCOME_MESSAGES_BEFORE_CONTRACT = [
  () => "건강한 정신 건강 문화를 만듭니다.",
  () => "곧 누군가의 마음 건강을 지키는 활동을 시작해요.",
  () => "디스턴싱이 필요한 분들에게 닿는 일, 함께 시작해요.",
  () => "누군가에게는 변화의 시작이 될 활동이 기다리고 있어요.",
  () => "디스턴싱과 함께 마음 건강을 전할 준비를 해봐요.",
];
const WELCOME_MESSAGES_AFTER_CONTRACT = [
  (_name: string) => "건강한 정신 건강 문화를 만듭니다.",
  (name: string) => `${name}님의 활동이 누군가의 마음 건강을 지키는 힘이 돼요.`,
  (name: string) =>
    `${name}님이 소개한 디스턴싱이 누군가에게는 변화의 시작이 돼요.`,
  (name: string) => `${name}님 덕분에 디스턴싱이 필요한 분들에게 닿고 있어요.`,
  (name: string) => `${name}님을 통해 누군가에게 큰 힘이되고 있어요.`,
  (name: string) => `오늘도 ${name}님과 함께 건강한 마음을 전하고 있어요.`,
  (name: string) => `${name}님의 활동 하나하나가 큰 힘이 돼요.`,
];

// 안내 배너 — 계약 체결·서류 제출 유도와 계약 완료 후 할 일(체험 계정·링크)이 같은 모양을 쓴다.
// 계약 유도는 페이지 이동, 할 일 안내는 같은 화면의 섹션으로 스크롤한다는 점만 다르다 (onClick 이 결정).
function GuideBanner({ text, onClick }: { text: string; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        mt: 3,
        cursor: "pointer",
        borderRadius: "12px",
        backgroundColor: theme.vars.palette.Surface.SoftHighlight,
        px: "16px",
        py: "12px",
      }}
    >
      <CustomTypography
        variant="Caption"
        sx={{
          fontWeight: 500,
          color: theme.vars.palette.Content.Highlight,
        }}
      >
        {text}
      </CustomTypography>
    </Box>
  );
}

// 환영 문구 — 첫 문구(브랜드 미션)로 시작해 30초마다 다음 문구로 순환, 400ms 페이드로 자연스럽게 전환한다.
function WelcomeMessage({
  name,
  contracted,
}: {
  name: string | null;
  contracted: boolean;
}) {
  const pool = contracted
    ? WELCOME_MESSAGES_AFTER_CONTRACT
    : WELCOME_MESSAGES_BEFORE_CONTRACT;
  // 시작 인덱스 고정 — 계약 전이든 후든 진입 시 가장 먼저 보이는 문구는 풀의 첫 문구다.
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => i + 1);
        setVisible(true);
      }, 400);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <CustomTypography
      variant="Caption"
      sx={{
        // 문구별 줄 수 차이로 아래 카드가 오르내리지 않게 2줄 높이를 고정 확보
        minHeight: "40px",
        fontWeight: 500,
        color: theme.vars.palette.Content.Medium,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.4s ease",
      }}
    >
      {pool[index % pool.length](name ?? "앰버서더")}
    </CustomTypography>
  );
}

// 레퍼럴 앰버서더(인플루언서) 성과 대시보드 — /with/:code 링크의 가입자/결제자/환불자를 total·일별·월별로 본다.
// 링크가 없으면 셀프 생성 UI 를, 계약 정보 미제출이면 입력 유도 배너를 보여준다.
// 토큰 만료(401)는 ambassadorAxios 인터셉터가 로그인 리다이렉트로 처리한다.
export default function AmbassadorDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState<Tab>("daily");
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // 상단 안내 띠가 가리킬 섹션 — 섹션은 조건에 따라 스스로 사라지므로(신청 확인·링크 생성 등)
  // 렌더 조건을 여기서 흉내내지 않고 ref 콜백으로 마운트 상태를 그대로 받는다 (콜백은 고정 identity 여야 한다).
  const [guideSections, setGuideSections] = useState<
    Record<GuideKey, HTMLElement | null>
  >({ trial: null, link: null });
  const registerTrialSection = useCallback(
    (element: HTMLElement | null) =>
      setGuideSections((prev) =>
        prev.trial === element ? prev : { ...prev, trial: element }
      ),
    []
  );
  const registerLinkSection = useCallback(
    (element: HTMLElement | null) =>
      setGuideSections((prev) =>
        prev.link === element ? prev : { ...prev, link: element }
      ),
    []
  );
  const [highlightedGuide, setHighlightedGuide] = useState<GuideKey | null>(
    null
  );
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
    },
    []
  );

  // 내 링크 풀 URL 복사 (예: https://distancing.im/with/CODE)
  const copyLink = async (code: string) => {
    const link = `${window.location.origin}/with/${code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1500);
    } catch (e) {
      // clipboard 미지원 브라우저 — 링크 텍스트가 select-all 이라 수동 복사 가능
    }
  };

  const hasToken = !!getAmbassadorToken();

  const { data, isLoading, isError, refetch } = useQuery(
    ["referral-ambassador-dashboard"],
    () => ambassadorApi.dashboard(),
    { enabled: hasToken, retry: false, refetchOnWindowFocus: false }
  );

  // 계약 정보 제출 여부 (미제출 배너용) — 파일까지 내려오지만 호출은 대시보드 진입 시 1회뿐이다.
  const { data: contractData } = useQuery(
    ["referral-ambassador-contract"],
    () => ambassadorApi.getContract(),
    { enabled: hasToken, retry: false, refetchOnWindowFocus: false }
  );

  if (!hasToken) {
    // 로그인 후 이 페이지로 복귀하도록 출발지를 state 로 전달한다 (로그인 페이지가 해석)
    return (
      <Navigate
        to={PATH.ambassadorLogin}
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  const handleLogout = () => {
    clearAmbassadorToken();
    navigate(PATH.ambassadorLogin, { replace: true });
  };

  const moveToGuideSection = (key: GuideKey) => {
    const element = guideSections[key];
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedGuide(key);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(
      () => setHighlightedGuide(null),
      GUIDE_HIGHLIGHT_MS
    );
  };

  const result = data?.code === 0 ? data.result : undefined;
  const failed = isError || (data !== undefined && data.code !== 0);
  const buckets: AmbassadorStatsBucket[] =
    (tab === "daily" ? result?.daily : result?.monthly) ?? [];
  const contractSubmitted =
    contractData?.code === 0 && !!contractData.result?.type;
  // 계약 완료 = 어드민이 체결 확인 후 표시 — 링크 생성 게이트·상태 칩·환영 문구 풀 분기 기준
  const contractCompleted =
    contractData?.code === 0 && !!contractData.result?.completed;
  // 전자계약 체결 여부 — 체결 후에는 정산 정보·서류 제출(통장사본 존재 여부로 판별)이 다음 단계다
  const contractSigned =
    contractData?.code === 0 && !!contractData.result?.signedAt;
  const documentsSubmitted =
    contractData?.code === 0 && !!contractData.result?.bankbookFile;
  // 유도 배너 — 미체결(구 플로우 제출 건 제외)이면 전자계약, 체결 후 서류 미제출이면 정산 정보 제출
  const showSignBanner =
    !!result &&
    contractData?.code === 0 &&
    !contractSigned &&
    !contractSubmitted;
  const showDocumentsBanner =
    !!result &&
    contractData?.code === 0 &&
    contractSigned &&
    !documentsSubmitted &&
    !contractCompleted;
  const showSettlement = tab === "monthly";

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: "720px",
          px: "20px",
          py: "40px",
          // 푸터 로고를 화면(콘텐츠) 최하단에 붙이기 위한 컬럼 레이아웃
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {/* 브랜드 헤더 — "디스턴싱 앰버서더" 소속 표현 + 계약 상태 칩 (로고는 푸터에 은은하게) */}
          <Stack direction="row" alignItems="center" spacing={"8px"}>
            <CustomTypography
              variant="H3"
              sx={{
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
              }}
            >
              {"디스턴싱 앰버서더"}
            </CustomTypography>
            {contractData?.code === 0 && (
              // 전자계약 플로우 상태: 체결 전 → 정산 정보 제출 전 → 계약 확인 중 → 활동 중(계약 완료).
              // 구 플로우(외부 체결 진행 중, 미체결·제출됨)는 기존 "계약 전" 유지.
              // 완료 전에만 클릭 시 다음 단계 페이지로 이동 — 활동 중 상태에서는 이동 없음
              <StatusChip
                positive={contractCompleted}
                label={
                  contractCompleted
                    ? "활동 중"
                    : contractSigned
                      ? documentsSubmitted
                        ? "계약 확인 중"
                        : "정산 정보 제출 전"
                      : contractSubmitted
                        ? "계약 전"
                        : "전자계약 체결 전"
                }
                onClick={
                  contractCompleted
                    ? undefined
                    : () =>
                        navigate(
                          contractSigned || contractSubmitted
                            ? PATH.ambassadorContract
                            : PATH.ambassadorContractSign
                        )
                }
              />
            )}
          </Stack>
          <AccountMenu
            onLink={() => navigate(PATH.ambassadorLink)}
            onContract={() => navigate(PATH.ambassadorContract)}
            onLogout={handleLogout}
          />
        </Stack>

        {/* 환영 문구·링크 목록 — 계정 관리 버튼과 같은 행에 두지 않고 full width 로 노출 */}
        {result && (
          <Stack spacing={"4px"} sx={{ mt: "8px" }}>
            {/* 계약 여부 로드 후에 노출 — 풀이 중간에 바뀌며 문구가 튀는 것을 방지 */}
            {contractData?.code === 0 && (
              <WelcomeMessage
                name={result.name ?? null}
                contracted={contractCompleted}
              />
            )}
            {(result.codeInfos ?? []).map(({ code, isActive }) => (
              <Stack
                key={code}
                direction="row"
                alignItems="center"
                spacing={"8px"}
              >
                {/* 주소 자체가 링크 — 눌러서 새 탭으로 열어 진입 화면을 확인할 수 있다 */}
                <CustomTypography
                  variant="Caption"
                  sx={{
                    fontWeight: 500,
                    color: isActive
                      ? theme.vars.palette.Content.Medium
                      : theme.vars.palette.Content.Weak,
                    userSelect: "all",
                    wordBreak: "break-all",
                  }}
                >
                  <Box
                    component="a"
                    href={`${window.location.origin}/with/${code}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "inherit", textDecoration: "underline" }}
                  >
                    {`${window.location.origin}/with/${code}`}
                  </Box>
                </CustomTypography>
                {/* 링크 복사 — 항상 노출, 복사 직후엔 체크 아이콘으로 피드백 */}
                {copiedCode === code ? (
                  <CheckRoundedIcon
                    sx={{
                      flexShrink: 0,
                      fontSize: "18px",
                      color: "#349434",
                    }}
                  />
                ) : (
                  <ContentCopyRoundedIcon
                    titleAccess="링크 복사"
                    onClick={() => copyLink(code)}
                    sx={{
                      flexShrink: 0,
                      fontSize: "18px",
                      color: theme.vars.palette.Content.Medium,
                      cursor: "pointer",
                    }}
                  />
                )}
                {/* 미승인(비활성) 코드는 상태 칩을 함께 표시 */}
                {!isActive && <CodeStatusChip isActive={false} />}
              </Stack>
            ))}
          </Stack>
        )}

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: "80px" }}>
            <CircularProgress color="neutral" />
          </Box>
        )}
        {failed && (
          <CustomTypography
            variant="Caption"
            sx={{
              mt: 5,
              textAlign: "center",
              fontWeight: 500,
              color: theme.vars.palette.Content.HighlightWithAlert,
            }}
          >
            {"데이터를 불러오지 못했어요. 잠시 후 다시 시도해 주세요."}
          </CustomTypography>
        )}

        {(showSignBanner || showDocumentsBanner) && (
          <GuideBanner
            text={
              showSignBanner
                ? "전자계약을 체결해 주세요 — 앰버서더 활동의 시작이에요 →"
                : "정산 정보와 서류를 제출해 주세요 — 확인 후 계약이 완료돼요 →"
            }
            onClick={() =>
              navigate(
                showSignBanner
                  ? PATH.ambassadorContractSign
                  : PATH.ambassadorContract
              )
            }
          />
        )}

        {/* 계약 완료 후 할 일 — 아래 섹션이 실제로 있을 때만 안내한다 (누르면 그 섹션으로 스크롤 + 잠깐 강조) */}
        {(["trial", "link"] as GuideKey[])
          .filter((key) => guideSections[key])
          .map((key) => (
            <GuideBanner
              key={key}
              text={GUIDE_BANNER_TEXTS[key]}
              onClick={() => moveToGuideSection(key)}
            />
          ))}

        {/* 프로그램 지원 신청 — 링크 섹션 위. 링크와 마찬가지로 계약 완료 이후에만 노출한다 */}
        {result && contractCompleted && (
          <TrialAccountSection
            sectionRef={registerTrialSection}
            highlighted={highlightedGuide === "trial"}
          />
        )}

        {/* 링크 생성은 계약 완료 이후에만 — 그 전에는 만들 수 있다는 것만 간접 안내 */}
        {result && result.codes.length === 0 && contractCompleted && (
          <CreateLinkSection
            initialName={result.name ?? ""}
            onCreated={() => refetch()}
            sectionRef={registerLinkSection}
            highlighted={highlightedGuide === "link"}
          />
        )}
        {result &&
          result.codes.length === 0 &&
          contractData?.code === 0 &&
          !contractCompleted && (
            <Stack
              spacing={"4px"}
              sx={{
                mt: 3,
                borderRadius: "16px",
                backgroundColor: theme.vars.palette.Surface.Forward,
                p: "20px",
              }}
            >
              <CustomTypography
                variant="Body"
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"내 링크 만들기"}
              </CustomTypography>
              <CustomTypography
                variant="Footnote"
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Weak,
                }}
              >
                {"계약이 완료되면 공유할 나만의 링크를 만들 수 있어요."}
              </CustomTypography>
            </Stack>
          )}

        {result && (
          <>
            <Stack direction="row" spacing={"12px"} sx={{ mt: 3 }}>
              <SummaryCard label="총 가입자" value={result.total.signupCount} />
              {/* 서버가 이미 유효 결제 건수를 준다 — 여기서 환불자를 또 빼면 이중 차감이다 */}
              <SummaryCard
                label="총 유효 결제자"
                value={result.total.payerCount}
                help="결제일로부터 7일 안에 환불한 사용자를 제외한 결제자 수예요. 7일이 지난 뒤의 환불은 빼지 않아요."
              />
              {result.total.settlementAmount != null && (
                <SummaryCard
                  label="총 정산액"
                  value={result.total.settlementAmount}
                  unit="원"
                  help="매월 9일에 전월 정산이 확정되고 10일에 지급돼요. 확정 전인 달은 환불에 따라 금액이 달라질 수 있어요."
                />
              )}
            </Stack>

            <Box sx={{ mt: 4 }}>
              <TabButtonList
                selectedTab={tab}
                data={[
                  {
                    id: "daily",
                    label: "일별",
                    onClick: () => setTab("daily"),
                  },
                  {
                    id: "monthly",
                    label: "월별",
                    onClick: () => setTab("monthly"),
                  },
                ]}
              />
            </Box>

            {/* 모바일에서 칼럼 헤더가 줄바꿈되지 않도록 — 최소 폭을 확보하고 가로 스크롤로 처리 */}
            <Box
              sx={{
                mt: "12px",
                borderRadius: "16px",
                backgroundColor: theme.vars.palette.Surface.Forward,
                p: 1,
                overflowX: "auto",
              }}
            >
              <Stack sx={{ minWidth: showSettlement ? "560px" : "420px" }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ height: "40px", px: "12px" }}
                >
                  <StatCell flex={1.2}>
                    <CustomTypography
                      variant="Caption"
                      sx={{
                        fontWeight: 500,
                        color: theme.vars.palette.Content.Weak,
                      }}
                    >
                      {tab === "daily" ? "날짜" : "월"}
                    </CustomTypography>
                  </StatCell>
                  {[
                    {
                      label: "가입자",
                      help: "링크로 유입되어 코드가 연결된 사용자 수예요.",
                    },
                    // 일별은 지표라 "몇 명이 결제했나"이고, 월별은 지급 기준이라 유효 결제/제외다.
                    // 같은 이름에 다른 뜻을 담지 않으려고 탭마다 라벨을 나눈다.
                    ...(tab === "monthly"
                      ? [
                          {
                            label: "유효 결제",
                            help: "결제일로부터 7일 안에 환불이 없어 정산 대상이 된 결제 건수예요.",
                          },
                          {
                            label: "제외",
                            help: "7일 안에 환불이 있어 정산에서 빠진 결제 건수예요. 7일이 지난 뒤의 환불은 빼지 않아요.",
                          },
                        ]
                      : [
                          {
                            label: "결제자",
                            help: "마지막 링크 진입 후 3일 내 결제로 기여가 확정된 사용자 수예요.",
                          },
                          {
                            label: "환불자",
                            help: "결제자 중 결제를 취소한 사용자 수예요.",
                          },
                        ]),
                  ].map(({ label, help }) => (
                    <StatCell key={label} flex={1} alignRight>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <CustomTypography
                          variant="Caption"
                          sx={{
                            fontWeight: 500,
                            color: theme.vars.palette.Content.Weak,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {label}
                        </CustomTypography>
                        <InfoTip help={help} />
                      </Box>
                    </StatCell>
                  ))}
                  {showSettlement && (
                    <StatCell flex={1.2} alignRight>
                      <CustomTypography
                        variant="Caption"
                        sx={{
                          fontWeight: 500,
                          color: theme.vars.palette.Content.Weak,
                        }}
                      >
                        {"정산액"}
                      </CustomTypography>
                    </StatCell>
                  )}
                </Stack>

                {buckets.length === 0 && (
                  <CustomTypography
                    variant="Caption"
                    sx={{
                      py: "24px",
                      textAlign: "center",
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Weak,
                    }}
                  >
                    {"아직 집계된 데이터가 없어요"}
                  </CustomTypography>
                )}
                {buckets.map((bucket) => (
                  <Stack
                    key={bucket.period}
                    direction="row"
                    alignItems="center"
                    sx={{ height: "48px", px: "12px" }}
                  >
                    <StatCell flex={1.2}>
                      <CustomTypography
                        variant="Body"
                        sx={{
                          fontWeight: 500,
                          color: theme.vars.palette.Content.Medium,
                        }}
                      >
                        {bucket.period}
                      </CustomTypography>
                    </StatCell>
                    {[
                      bucket.signupCount,
                      bucket.payerCount,
                      bucket.refundCount,
                    ].map((count, index) => (
                      <StatCell key={index} flex={1} alignRight>
                        <CustomTypography
                          variant="Body"
                          sx={{
                            fontWeight: 500,
                            color: theme.vars.palette.Content.Strong,
                          }}
                        >
                          {count}
                        </CustomTypography>
                      </StatCell>
                    ))}
                    {showSettlement && (
                      <StatCell flex={1.2} alignRight>
                        <CustomTypography
                          variant="Body"
                          sx={{
                            fontWeight: 700,
                            color: theme.vars.palette.Content.Strong,
                          }}
                        >
                          {bucket.settlementAmount != null
                            ? `${bucket.settlementAmount.toLocaleString()}원`
                            : "-"}
                        </CustomTypography>
                        {/* 확정 전 금액은 환불로 달라질 수 있다 — 지급받을 금액으로 오해하지 않도록 표기한다.
                            확정(익월 9일)된 달에는 아무 표기도 붙이지 않는다: 그게 기본값이어야 한다. */}
                        {tab === "monthly" &&
                          bucket.settlementAmount != null &&
                          !bucket.confirmed && (
                            <CustomTypography
                              variant="Caption"
                              sx={{
                                fontWeight: 400,
                                color: theme.vars.palette.Content.Weak,
                              }}
                            >
                              {"확정 전"}
                            </CustomTypography>
                          )}
                      </StatCell>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* 활동 참고 자료 — 공식 채널·프로그램 소개. 오픈 여부 미정이라 일단 숨김 */}
            <Stack
              spacing={"12px"}
              sx={{
                display: "none",
                mt: 3,
                borderRadius: "16px",
                backgroundColor: theme.vars.palette.Surface.Forward,
                p: "20px",
              }}
            >
              <CustomTypography
                variant="Body"
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"활동에 참고해 보세요"}
              </CustomTypography>
              {[
                {
                  title: "디스턴싱 공식 유튜브",
                  description:
                    "공식 채널의 영상을 콘텐츠 제작에 참고할 수 있어요.",
                  url: "https://www.youtube.com/@distancing",
                },
                {
                  title: "디스턴싱 프로그램 소개",
                  description:
                    "프로그램의 개념과 구성을 이해하는 데 도움이 돼요.",
                  url: "https://orwell.distancing.im/program",
                },
              ].map(({ title, description, url }) => (
                <Box
                  key={url}
                  onClick={() => window.open(url)}
                  sx={{ cursor: "pointer" }}
                >
                  <CustomTypography
                    variant="Caption"
                    sx={{
                      fontWeight: 700,
                      color: theme.vars.palette.Content.Strong,
                      textDecoration: "underline",
                    }}
                  >
                    {`${title} →`}
                  </CustomTypography>
                  <CustomTypography
                    variant="Footnote"
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Weak,
                    }}
                  >
                    {description}
                  </CustomTypography>
                </Box>
              ))}
            </Stack>
          </>
        )}

        {/* 푸터 — 로고를 은은하게, mt auto 로 항상 하단에 고정 */}
        <Box
          sx={{ display: "flex", justifyContent: "center", mt: "auto", pt: 6 }}
        >
          <img
            src={distancingLogo}
            alt="distancing logo"
            style={{ width: "80px", height: "auto", opacity: 0.35 }}
          />
        </Box>
      </Box>
    </Box>
  );
}

function StatCell({
  flex,
  alignRight,
  children,
}: {
  flex: number;
  alignRight?: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        flex,
        display: "flex",
        justifyContent: alignRight ? "flex-end" : "flex-start",
      }}
    >
      {children}
    </Box>
  );
}

// 안내를 확인 처리한 기록의 저장 키 — 계정이 아닌 기기 기준 기록이라 서버 상태를 대신하지 않는다.
// 값은 "회원번호:이용상태" — 다른 계정으로 다시 지원받거나 이용이 종료되면 안내가 새로 노출된다.
const TRIAL_ACK_STORAGE_KEY = "ambassador_trial_acknowledged_user_id";

function readTrialAcknowledged(): string | null {
  try {
    return localStorage.getItem(TRIAL_ACK_STORAGE_KEY);
  } catch (e) {
    // 프라이빗 모드 등 localStorage 차단 — 확인 기록 없이 매번 노출된다
    return null;
  }
}

function writeTrialAcknowledged(ackKey: string) {
  try {
    localStorage.setItem(TRIAL_ACK_STORAGE_KEY, ackKey);
  } catch (e) {
    // 저장 실패해도 이번 세션의 hide 동작은 그대로 진행한다
  }
}

// 프로그램 지원 신청 — 앰버서더가 이용할 계정의 회원번호(Long)+닉네임을 신청하면 담당자(어드민) 확인 후 코치 배정·프로그램이 세팅된다.
// (서버·어드민은 여전히 "체험(trial)" 용어를 쓴다 — 앰버서더 노출 문구만 실제 서비스 제공 톤으로 표기)
// 상태 3단계: 신청 전(입력 폼) → 확인 중(담당자 확인 대기) → 준비 완료. 상태 소스는 GET /ambassador/trial.
function TrialAccountSection({
  sectionRef,
  highlighted,
}: {
  // 안내 배너가 가리킬 대상 — 안내가 필요한 상태(신청 전)에서만 연결한다.
  // 연결이 끊기면(null 호출) 배너도 함께 사라진다 — 배너 노출 조건을 대시보드에서 중복 판단하지 않는다.
  sectionRef: (element: HTMLElement | null) => void;
  highlighted: boolean;
}) {
  const { data, refetch } = useQuery(
    ["referral-ambassador-trial"],
    () => ambassadorApi.getTrial(),
    { retry: false, refetchOnWindowFocus: false }
  );
  const [userIdInput, setUserIdInput] = useState("");
  const [nickname, setNickname] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // 신청 전 확인 모달 — 회원번호 오입력 방지를 위해 입력값을 한 번 더 보여준다
  const [confirming, setConfirming] = useState(false);
  // 준비 완료를 앰버서더가 확인한 회원번호 (기기별 기록) — 확인 후에는 대시보드에서 감추고 프로필 정보에서만 본다
  const [acknowledged, setAcknowledged] = useState(() =>
    readTrialAcknowledged()
  );
  // 콜랩스 애니메이션 중 — 페이드+높이 축소가 끝난 뒤 언마운트한다
  const [hiding, setHiding] = useState(false);

  // 회원번호는 서버 Long — 숫자만 유효
  const trialUserId = /^\d+$/.test(userIdInput.trim())
    ? Number(userIdInput.trim())
    : null;
  const valid =
    trialUserId !== null && trialUserId > 0 && nickname.trim().length > 0;

  const submit = async () => {
    if (!valid || submitting || trialUserId === null) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await ambassadorApi.requestTrial(
        trialUserId,
        nickname.trim()
      );
      setConfirming(false);
      if (res.code === 0) {
        refetch();
        return;
      }
      setMessage(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setConfirming(false);
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  // 상태를 모르는 동안(로딩/조회 실패)은 섹션을 그리지 않는다 — 잘못된 폼 노출 방지
  const trial = data?.code === 0 ? data.result : undefined;
  if (!trial) return null;

  // 확인 기록 키 — 회원번호 + 이용 상태. 준비 완료를 확인해 감췄더라도 이후 "이용 종료"는 새 안내라 다시 노출된다.
  // 이미 확인한 안내면 대시보드에서 감춘다 (정보는 프로필 정보 페이지에 남는다)
  const ackKey =
    trial.completed && trial.trialUserId != null
      ? `${trial.trialUserId}:${trial.ended ? "ended" : "active"}`
      : null;
  if (ackKey !== null && acknowledged === ackKey) return null;

  // 안내 배너는 신청 전에만 — 담당자 확인 중이거나 준비가 끝났으면 앰버서더가 할 일은 이미 없다.
  // 섹션은 그대로 두고(상태 확인용) 배너만 감춘다.
  const guideNeeded = !trial.requested && !trial.completed;

  // 확인 처리 — 콜랩스 애니메이션(400ms)이 끝난 뒤에 언마운트한다
  const acknowledge = () => {
    if (ackKey === null) return;
    writeTrialAcknowledged(ackKey);
    setHiding(true);
    setTimeout(() => setAcknowledged(ackKey), 400);
  };

  const inputSx = {
    // 로그인/계약 페이지와 동일한 입력 표준 (PromotionBookPhoneNumberSubmit 선례)
    width: "100%",
    height: "54px",
    borderRadius: "12px",
    backgroundColor: theme.vars.palette.Surface.Forward,
    fontSize: "16px",
    borderColor: "background.level3",
    borderWidth: "1px",
    "--joy-focus-thickness": "0px",
    boxShadow: "0px 0px 0px transparent",
    "&:focus-within": {
      boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`,
    },
  };

  return (
    <Stack
      ref={guideNeeded ? sectionRef : undefined}
      spacing={"12px"}
      sx={{
        borderRadius: "16px",
        backgroundColor: theme.vars.palette.Surface.Forward,
        // 확인 시 페이드 + 높이 축소로 부드럽게 사라진다 (maxHeight 는 실제 높이보다 넉넉한 상한)
        overflow: "hidden",
        transition:
          "max-height 0.4s ease, opacity 0.3s ease, margin-top 0.4s ease, padding 0.4s ease, box-shadow 0.3s ease",
        mt: hiding ? 0 : 3,
        maxHeight: hiding ? 0 : "1000px",
        opacity: hiding ? 0 : 1,
        p: hiding ? "0px 20px" : "20px",
        // 안내 띠로 이동해 왔을 때의 잠깐 강조 — 테두리 링만 켰다 끈다 (레이아웃 영향 없음)
        boxShadow: highlighted
          ? `0 0 0 2px ${theme.vars.palette.Content.Highlight}`
          : "0 0 0 0 transparent",
      }}
    >
      <Stack spacing={"4px"}>
        <Stack direction="row" alignItems="center" spacing={"8px"}>
          <CustomTypography
            variant="Body"
            sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
          >
            {"체험 계정 설정"}
          </CustomTypography>
          {/* 상태 라벨 — 신청 전/이용 중에는 칩 없음. 확인 대기(주황)·종료(중립 회색)만 노출 */}
          {trial.completed && trial.ended ? (
            <StatusChip neutral label="종료" />
          ) : !trial.completed && trial.requested ? (
            <StatusChip positive={false} label="담당자 확인 중" />
          ) : null}
        </Stack>
        <CustomTypography
          variant="Footnote"
          sx={{ fontWeight: 500, color: theme.vars.palette.Content.Weak }}
        >
          {trial.completed
            ? trial.ended
              ? "프로그램 이용이 종료됐어요. 다시 이용하고 싶으시면 담당자에게 문의해 주세요."
              : "프로그램이 준비됐어요. 앱에서 코치 선생님과 함께 시작해 보세요."
            : trial.requested
              ? "담당자가 확인한 뒤 프로그램을 준비해 드려요. 잠시만 기다려 주세요."
              : "앰버서더님이 디스턴싱을 직접 경험하실 수 있도록 무상으로 프로그램을 지원해 드려요. 실제 코치 선생님이 배정되고 사용자와 동일하게 진행돼요."}
        </CustomTypography>
      </Stack>

      {trial.completed || trial.requested ? (
        <>
          {trial.trialUserId != null && (
            <CustomTypography
              variant="Caption"
              sx={{ fontWeight: 500, color: theme.vars.palette.Content.Strong }}
            >
              {`회원번호 ${trial.trialUserId}${trial.nickname ? ` · 닉네임 ${trial.nickname}` : ""}`}
            </CustomTypography>
          )}
          {/* 준비 완료·이용 종료 안내는 한 번 확인하면 감춘다 — 이후에는 프로필 정보에서 확인 */}
          {ackKey !== null && (
            <Button
              variant="Soft"
              size="sm"
              onClick={acknowledge}
              sx={{
                alignSelf: "flex-end",
                fontWeight: 700,
                height: "40px",
                minHeight: "40px",
                fontSize: "14px",
              }}
            >
              {"확인했어요"}
            </Button>
          )}
        </>
      ) : (
        <Stack spacing={"8px"}>
          {/* 회원번호/닉네임 확인 방법 안내 */}
          <Box
            sx={{
              borderRadius: "12px",
              backgroundColor: theme.vars.palette.Background.Level1,
              px: "16px",
              py: "12px",
            }}
          >
            <CustomTypography
              variant="Footnote"
              sx={{
                fontWeight: 600,
                color: theme.vars.palette.Content.Strong,
              }}
            >
              {"계정 확인 방법"}
            </CustomTypography>

            <CustomTypography
              variant="Footnote"
              sx={{ fontWeight: 500, color: theme.vars.palette.Content.Weak }}
            >
              {
                '디스턴싱 앱 가입 → 온보딩 설문 끝까지 진행 → 결제 페이지 진입 → 가장 하단의 "계정" 버튼'
              }
            </CustomTypography>
          </Box>
          {/* 회원번호·닉네임 모두 짧은 값이라 한 줄에 나란히 받는다 */}
          <Stack direction="row" spacing={"8px"}>
            {/* minWidth 0 — flex 아이템 기본 min-width:auto 로 인한 부모 영역 초과 방지 */}
            <Stack spacing={"4px"} sx={{ flex: 1, minWidth: 0 }}>
              <CustomTypography
                variant="Footnote"
                sx={{
                  fontWeight: 600,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"회원번호"}
              </CustomTypography>
              <Input
                sx={inputSx}
                type="text"
                slotProps={{ input: { inputMode: "numeric" } }}
                placeholder="예: 12345"
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value)}
              />
            </Stack>
            <Stack spacing={"4px"} sx={{ flex: 1, minWidth: 0 }}>
              <CustomTypography
                variant="Footnote"
                sx={{
                  fontWeight: 600,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"닉네임"}
              </CustomTypography>
              <Input
                sx={inputSx}
                placeholder="예: 길동"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && valid && setConfirming(true)
                }
              />
            </Stack>
          </Stack>
          {userIdInput.trim().length > 0 && trialUserId === null && (
            <CustomTypography
              variant="Footnote"
              sx={{
                fontWeight: 500,
                color: theme.vars.palette.Content.HighlightWithAlert,
              }}
            >
              {"회원번호는 숫자만 입력해 주세요."}
            </CustomTypography>
          )}
          <Button
            variant="Action"
            size="sm"
            loading={submitting}
            disabled={!valid}
            onClick={() => setConfirming(true)}
            sx={{
              alignSelf: "flex-end",
              fontWeight: 700,
              height: "40px",
              minHeight: "40px",
              fontSize: "14px",
            }}
          >
            {"신청하기"}
          </Button>
        </Stack>
      )}

      {/* 신청 전 확인 모달 */}
      {confirming && (
        <CustomModal
          open
          onClose={() => setConfirming(false)}
          title="이 계정으로 신청할까요?"
          subtitle="담당자 확인 후 프로그램이 준비돼요."
          actions={[
            {
              label: "신청하기",
              onClick: submit,
              variant: "Solid",
              buttonProps: { loading: submitting },
            },
            {
              label: "취소",
              onClick: () => setConfirming(false),
              variant: "Outlined",
            },
          ]}
        >
          <CustomTypography
            variant="Body"
            sx={{
              fontWeight: 700,
              color: theme.vars.palette.Content.Strong,
              wordBreak: "break-all",
            }}
          >
            {`회원번호 ${trialUserId ?? ""} · ${nickname.trim()}`}
          </CustomTypography>
        </CustomModal>
      )}
      {message && (
        <CustomTypography
          variant="Caption"
          sx={{
            fontWeight: 500,
            color: theme.vars.palette.Content.HighlightWithAlert,
          }}
        >
          {message}
        </CustomTypography>
      )}
    </Stack>
  );
}

// 셀프 링크 생성 — 1인 1코드. 생성 후 대시보드를 다시 불러와 링크 목록을 갱신한다.
// 활동명(링크 노출 이름)도 여기서 함께 받는다 — 계약서 기재 대상이 아니라 링크를 만드는 시점이 자연스럽다.
function CreateLinkSection({
  initialName,
  onCreated,
  sectionRef,
  highlighted,
}: {
  initialName: string;
  onCreated: () => void;
  // 상단 안내 띠의 이동 대상 — 링크를 만들면 섹션과 함께 띠의 항목도 사라진다
  sectionRef: (element: HTMLElement | null) => void;
  highlighted: boolean;
}) {
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState(initialName);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // 생성 전 확인 모달 — 링크 주소는 만든 후 변경 불가라 한 번 더 확인시킨다
  const [confirming, setConfirming] = useState(false);

  const normalized = slug.trim().toLowerCase();
  const slugValid = CODE_SLUG_REGEX.test(normalized);
  const trimmedName = displayName.trim();
  const nameValid = trimmedName.length > 0 && trimmedName.length < 12;
  const valid = slugValid && nameValid;

  const create = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await ambassadorApi.createCode(normalized, trimmedName);
      if (res.code === 0) {
        setConfirming(false);
        onCreated();
        return;
      }
      setConfirming(false);
      setMessage(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setConfirming(false);
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack
      ref={sectionRef}
      spacing={"12px"}
      sx={{
        mt: 3,
        borderRadius: "16px",
        backgroundColor: theme.vars.palette.Surface.Forward,
        p: "20px",
        // 안내 띠로 이동해 왔을 때의 잠깐 강조 — 테두리 링만 켰다 끈다 (레이아웃 영향 없음)
        transition: "box-shadow 0.3s ease",
        boxShadow: highlighted
          ? `0 0 0 2px ${theme.vars.palette.Content.Highlight}`
          : "0 0 0 0 transparent",
      }}
    >
      <Stack spacing={"4px"}>
        <CustomTypography
          variant="Body"
          sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
        >
          {"내 링크 만들기"}
        </CustomTypography>
        <CustomTypography
          variant="Footnote"
          sx={{ fontWeight: 500, color: theme.vars.palette.Content.Weak }}
        >
          {
            "나만의 링크를 만드세요. 영문 소문자·숫자·하이픈(-)·언더스코어(_) 3~20자."
          }
        </CustomTypography>
      </Stack>
      {/* 활동명 — 링크 진입 페이지에 노출되는 이름. 계정(name)에 기록되며 이후 프로필 메뉴에서 수정 가능 */}
      <Stack spacing={"6px"}>
        <CustomTypography
          variant="Caption"
          sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
        >
          {"활동명"}
        </CustomTypography>
        <Input
          placeholder="링크에 노출될 이름 (12자 미만)"
          value={displayName}
          slotProps={{ input: { maxLength: 11 } }}
          onChange={(e) => setDisplayName(e.target.value)}
          sx={{
            width: "100%",
            height: "54px",
            borderRadius: "12px",
            backgroundColor: theme.vars.palette.Surface.Forward,
            fontSize: "16px",
            borderColor: "background.level3",
            borderWidth: "1px",
            "--joy-focus-thickness": "0px",
            boxShadow: "0px 0px 0px transparent",
            "&:focus-within": {
              boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`,
            },
          }}
        />
      </Stack>
      {/* 입력창에는 코드만 받고, 완성될 주소는 입력창 위에 실시간 미리보기로 보여준다 */}
      <Stack spacing={"8px"}>
        <CustomTypography
          variant="Caption"
          sx={{
            fontWeight: 500,
            color: theme.vars.palette.Content.Strong,
            wordBreak: "break-all",
          }}
        >
          {`링크: ${window.location.host}/with/${slug || "코드"}`}
        </CustomTypography>
        <Input
          placeholder="코드 입력 (예: mycode)"
          value={slug}
          // 입력을 막지 않고 그대로 받는다 — 형식 오류는 아래 안내 문구로 보여준다 (대문자는 제출 시 소문자 변환)
          onChange={(e) => setSlug(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && valid && setConfirming(true)}
          sx={{
            // 로그인/계약 페이지와 동일한 입력 표준 (PromotionBookPhoneNumberSubmit 선례)
            width: "100%",
            height: "54px",
            borderRadius: "12px",
            backgroundColor: theme.vars.palette.Surface.Forward,
            fontSize: "16px",
            borderColor: "background.level3",
            borderWidth: "1px",
            "--joy-focus-thickness": "0px",
            boxShadow: "0px 0px 0px transparent",
            "&:focus-within": {
              boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`,
            },
          }}
        />
        {/* 변경 불가 경고 — 인풋 바로 아래에 강조해서 보여준다 */}
        <CustomTypography
          variant="Footnote"
          sx={{ fontWeight: 700, color: theme.vars.palette.text.orange }}
        >
          {"*링크는 변경할 수 없으니 신중히 만들어 주세요."}
        </CustomTypography>
        <Button
          variant="Action"
          size="sm"
          loading={submitting}
          disabled={!valid}
          onClick={() => setConfirming(true)}
          sx={{
            alignSelf: "flex-end",
            fontWeight: 700,
            height: "40px",
            minHeight: "40px",
            fontSize: "14px",
          }}
        >
          {"만들기"}
        </Button>
      </Stack>

      {/* 생성 전 확인 모달 */}
      {confirming && (
        <CustomModal
          open
          onClose={() => setConfirming(false)}
          title="이 주소로 링크를 만들까요?"
          subtitle="링크는 만든 후 변경할 수 없어요."
          actions={[
            {
              label: "만들기",
              onClick: create,
              variant: "Solid",
              buttonProps: { loading: submitting },
            },
            {
              label: "취소",
              onClick: () => setConfirming(false),
              variant: "Outlined",
            },
          ]}
        >
          <Stack spacing={"4px"}>
            <CustomTypography
              variant="Body"
              sx={{
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
                wordBreak: "break-all",
              }}
            >
              {`${window.location.host}/with/${normalized}`}
            </CustomTypography>
            <CustomTypography
              variant="Caption"
              sx={{ fontWeight: 500, color: theme.vars.palette.Content.Medium }}
            >
              {`활동명: ${trimmedName}`}
            </CustomTypography>
          </Stack>
        </CustomModal>
      )}
      {slug.length > 0 && !slugValid && (
        <CustomTypography
          variant="Footnote"
          sx={{
            fontWeight: 500,
            color: theme.vars.palette.Content.HighlightWithAlert,
          }}
        >
          {
            "영문 소문자·숫자·하이픈·언더스코어만 사용해 3~20자로 입력해 주세요."
          }
        </CustomTypography>
      )}
      {message && (
        <CustomTypography
          variant="Caption"
          sx={{
            fontWeight: 500,
            color: theme.vars.palette.Content.HighlightWithAlert,
          }}
        >
          {message}
        </CustomTypography>
      )}
    </Stack>
  );
}

// 도움말 ⓘ 아이콘 — 툴팁을 제어 모드로: 기본 동작은 hover/길게누르기라 모바일에서 발견이 어렵다.
// 탭(클릭)으로 토글하고, 모바일에서 닫을 수단이 마땅치 않으니 4초 후 자동으로 닫는다.
// hover 핸들러는 달지 않는다 — 모바일 탭이 합성 mouseenter(열림)→click(토글 닫힘)으로
// 이어져 첫 탭에 떴다가 바로 사라지는 문제가 생긴다.
function InfoTip({ help }: { help: string }) {
  const [open, setOpen] = useState(false);
  const iconRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setOpen(false), 4000);
    // 바깥(다른 툴팁 아이콘 포함)을 탭하면 닫는다 — 자기 아이콘 탭은 onClick 토글이 처리.
    // 리스너는 열린 뒤(effect 시점)에 붙으므로 여는 탭 자체에는 반응하지 않는다.
    const closeOnOutsideClick = (e: MouseEvent) => {
      if (
        iconRef.current &&
        e.target instanceof Node &&
        iconRef.current.contains(e.target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener("click", closeOnOutsideClick);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", closeOnOutsideClick);
    };
  }, [open]);

  return (
    <Tooltip title={help} placement="top" arrow open={open}>
      <InfoOutlinedIcon
        ref={iconRef}
        onClick={() => setOpen((v) => !v)}
        sx={{
          fontSize: "14px",
          color: theme.vars.palette.Content.Weak,
          cursor: "help",
        }}
      />
    </Tooltip>
  );
}

function SummaryCard({
  label,
  value,
  unit = "명",
  help,
}: {
  label: string;
  value: number;
  unit?: string;
  help?: string;
}) {
  return (
    <Stack
      spacing={"4px"}
      sx={{
        flex: 1,
        borderRadius: "16px",
        backgroundColor: theme.vars.palette.Surface.Forward,
        px: "16px",
        py: "20px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <CustomTypography
          variant="Caption"
          sx={{ fontWeight: 500, color: theme.vars.palette.Content.Weak }}
        >
          {label}
        </CustomTypography>
        {help && <InfoTip help={help} />}
      </Box>
      <CustomTypography
        variant="H3"
        sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
      >
        {`${value.toLocaleString()}${unit}`}
      </CustomTypography>
    </Stack>
  );
}

// 계정 관리 메뉴 — 프로필 정보 · 계약 정보 · 로그아웃 (비밀번호 변경은 알림톡 인증 기반 재설정으로 통합 예정이라 제외)
function AccountMenu({
  onLink,
  onContract,
  onLogout,
}: {
  onLink: () => void;
  onContract: () => void;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);

  const menuItemSx = {
    justifyContent: "flex-start",
    fontWeight: 500,
    color: theme.vars.palette.Content.Medium,
  };

  return (
    <Box sx={{ position: "relative", flexShrink: 0 }}>
      {/* 계정 관리 — 아이콘 트리거 */}
      <PersonIcon
        titleAccess="계정 관리"
        onClick={() => setOpen(!open)}
        sx={{
          fontSize: "24px",
          color: theme.vars.palette.Content.Weak,
          cursor: "pointer",
        }}
      />
      {open && (
        // 빈 공간(오버레이) 클릭 시 메뉴 닫기 — 메뉴(zIndex 10)보다 아래에 깔린다
        <Box
          onClick={() => setOpen(false)}
          sx={{ position: "fixed", inset: 0, zIndex: 9 }}
        />
      )}
      {open && (
        <Stack
          spacing={"2px"}
          sx={{
            position: "absolute",
            right: 0,
            zIndex: 10,
            mt: 1,
            width: "224px",
            borderRadius: "16px",
            backgroundColor: theme.vars.palette.Surface.Forward,
            boxShadow: theme.shadow.md,
            p: 1,
          }}
        >
          <Button
            variant="Plain"
            size="sm"
            sx={menuItemSx}
            onClick={() => {
              setOpen(false);
              onLink();
            }}
          >
            {"프로필 정보"}
          </Button>
          <Button
            variant="Plain"
            size="sm"
            sx={menuItemSx}
            onClick={() => {
              setOpen(false);
              onContract();
            }}
          >
            {"계약 정보"}
          </Button>
          <Button
            variant="Plain"
            size="sm"
            sx={{
              ...menuItemSx,
              color: theme.vars.palette.Content.HighlightWithAlert,
            }}
            onClick={onLogout}
          >
            {"로그아웃"}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
